import type {
  Combo,
  ComboInput,
  Execution,
  ExecutionInput,
  PlantCaption,
  PlantMedia,
  PlantSpot,
  PlantSpotInput,
  Scope,
} from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiFailure } from "./api.ts";
import { shrinkImage } from "./shrink-image.ts";
import { stuffKeys, uploadClip } from "./stuff-queries.ts";

/**
 * Keys sit under "stuffs": combos, executions and post-plants show singles (and my
 * status on them), so anything that changes a single refreshes them too.
 */
const keys = {
  combos: (scope: Scope, map: string) => ["stuffs", "combos", scope, map] as const,
  combo: (id: string) => ["stuffs", "combo", id] as const,
  executions: (scope: Scope, map: string) => ["stuffs", "executions", scope, map] as const,
  execution: (id: string) => ["stuffs", "execution", id] as const,
  plants: (scope: Scope, map: string) => ["stuffs", "plants", scope, map] as const,
  plant: (id: string) => ["stuffs", "plant", id] as const,
};

const qs = (scope: Scope, map: string) => `?scope=${encodeURIComponent(scope)}&map=${encodeURIComponent(map)}`;

export const useCombos = (scope: Scope, map: string) =>
  useQuery({ queryKey: keys.combos(scope, map), queryFn: () => api<Combo[]>("GET", `/combos${qs(scope, map)}`) });

export const useCombo = (id: string | undefined) =>
  useQuery({ queryKey: keys.combo(id ?? ""), queryFn: () => api<Combo>("GET", `/combos/${id}`), enabled: Boolean(id) });

export const useExecutions = (scope: Scope, map: string) =>
  useQuery({ queryKey: keys.executions(scope, map), queryFn: () => api<Execution[]>("GET", `/executions${qs(scope, map)}`) });

export const useExecution = (id: string | undefined) =>
  useQuery({ queryKey: keys.execution(id ?? ""), queryFn: () => api<Execution>("GET", `/executions/${id}`), enabled: Boolean(id) });

export const usePlantSpots = (scope: Scope, map: string) =>
  useQuery({ queryKey: keys.plants(scope, map), queryFn: () => api<PlantSpot[]>("GET", `/plant-spots${qs(scope, map)}`) });

export const usePlantSpot = (id: string | undefined) =>
  useQuery({ queryKey: keys.plant(id ?? ""), queryFn: () => api<PlantSpot>("GET", `/plant-spots/${id}`), enabled: Boolean(id) });

/** Create (no id) or replace (id) one of the three, then refresh everything that shows singles. */
function useSave<I, O>(path: string, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I & { scope?: Scope }) => (id ? api<O>("PUT", `${path}/${id}`, input) : api<O>("POST", path, input)),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}

function useRemove(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `${path}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}

export const useSaveCombo = (id?: string) => useSave<ComboInput, Combo>("/combos", id);

/** A combo's own video of the whole sequence. */
export const uploadComboClip = (comboId: string, file: File, onProgress: (done: number) => void) => uploadClip(comboId, file, onProgress, `/api/combos/${comboId}/clip`);

export function useRemoveComboClip(comboId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("DELETE", `/combos/${comboId}/clip`),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}
export const useDeleteCombo = () => useRemove("/combos");
export const useSaveExecution = (id?: string) => useSave<ExecutionInput, Execution>("/executions", id);
export const useDeleteExecution = () => useRemove("/executions");
export const useSavePlantSpot = (id?: string) => useSave<PlantSpotInput, PlantSpot>("/plant-spots", id);
export const useDeletePlantSpot = () => useRemove("/plant-spots");

/** Adds a picture to a plant spot, shrunk first if big. */
export async function uploadPlantPicture(id: string, original: File, caption: PlantCaption): Promise<PlantMedia> {
  const file = await shrinkImage(original);
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/plant-spots/${id}/media?caption=${caption}`, { method: "POST", body: form, credentials: "same-origin" });
  const data = (await res.json().catch(() => null)) as { code?: ApiFailure["code"]; message?: string } | null;
  // A proxy in front of the API (ingress body limit) answers 413 in HTML, without our JSON.
  if (res.status === 413 && !data?.code) throw new ApiFailure(413, "too_large", "Too large");
  if (!res.ok) throw new ApiFailure(res.status, data?.code ?? "internal", data?.message ?? res.statusText);
  return data as unknown as PlantMedia;
}

export function useRemovePlantPicture(plantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => api<void>("DELETE", `/plant-spots/${plantId}/media/${mediaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}
